import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { TranscriptEditorChangeType } from '../types';
import { ITextElement } from '../models/interfaces';
import { TranscriptElementFactory } from '../models/TranscriptElementFactory';

/**
 * Command to insert a new text element after the current selection
 * 
 * This command creates a new text element after the selected element
 * with a timestamp between the selection end time and the next element's start time.
 */
export class InsertTextCommand extends AbstractTranscriptEditorCommand {
  /** Text to insert */
  private readonly newText: string;
  
  /** Element after which to insert the new text */
  private readonly afterElement: {
    id: string;
    segmentId: string;
    paragraphId: string;
    type: string;
  };
  
  /** New element ID created by this command */
  private newElementId: string | null = null;
  
  /** Element before which the new text was inserted (for undo) */
  private beforeElement: {
    id: string;
    segmentId: string;
    paragraphId: string;
    type: string;
  } | null = null;
  
  /** ID of the user performing the operation */
  private readonly userId: string;

  /**
   * Create a new InsertTextCommand
   * 
   * @param editor - The transcript editor instance
   * @param afterElement - Element after which to insert the new text
   * @param newText - The text to insert
   * @param userId - The ID of the user performing the operation
   */
  constructor(
    editor: TranscriptEditorCore,
    afterElement: {
      id: string;
      segmentId: string;
      paragraphId: string;
      type: string;
    },
    newText: string,
    userId: string
  ) {
    super(`Insert text "${newText}" after element`, editor);
    
    this.afterElement = afterElement;
    this.newText = newText;
    this.userId = userId;
    
    // Store metadata for debugging and tracking
    this.setMetadata('newText', newText);
    this.setMetadata('userId', userId);
    this.setMetadata('afterElementId', afterElement.id);
  }

  /**
   * Executes the command to insert text
   * 
   * @returns Promise resolving to true if successful
   */
  public async execute(): Promise<boolean> {
    try {
      // Start batch operation for better performance
      this.editor.beginBatch();

      // Get container
      const container = this.editor.getContainer();
      if (!container) {
        return false;
      }

      // Get segment and paragraph
      const segment = container.getSegment(this.afterElement.segmentId);
      if (!segment) {
        return false;
      }
      
      const paragraph = segment.getParagraph(this.afterElement.paragraphId);
      if (!paragraph) {
        return false;
      }
      
      // Get the element after which we're inserting
      const elementAfter = paragraph.getElement(this.afterElement.id);
      if (!elementAfter) {
        return false;
      }

      // Get timing information for the new element
      let startTime: number | undefined;
      let endTime: number | undefined;
      
      if ('endTime' in elementAfter && elementAfter.endTime !== undefined) {
        startTime = elementAfter.endTime;
      }
      
      // Find the next element to determine end time
      const paragraphElements = paragraph.elements;
      const elementIndex = paragraphElements.findIndex((e: any) => e.id === this.afterElement.id);
      
      if (elementIndex !== -1 && elementIndex < paragraphElements.length - 1) {
        const nextElement = paragraphElements[elementIndex + 1];
        this.beforeElement = {
          id: nextElement.id,
          segmentId: this.afterElement.segmentId,
          paragraphId: this.afterElement.paragraphId,
          type: nextElement.type,
        };
        
        if ('startTime' in nextElement && 
            typeof nextElement.startTime === 'number' && 
            !isNaN(nextElement.startTime)) {
          endTime = nextElement.startTime;
        }
      }
      
      // If we don't have both timing points, estimate a duration
      if (startTime !== undefined && endTime === undefined) {
        // Default to 1 second duration if we don't have an end time
        endTime = startTime + 1;
      } else if (startTime === undefined && endTime !== undefined) {
        // Default to 1 second before end time if we don't have a start time
        startTime = Math.max(0, endTime - 1);
      } else if (startTime === undefined && endTime === undefined) {
        // If we have no timing info, try to use the element's time
        if ('startTime' in elementAfter && elementAfter.startTime !== undefined) {
          startTime = elementAfter.startTime + 1;
          endTime = (startTime || 0) + 1;
        } else {
          // Last resort, just use 0 and 1
          startTime = 0;
          endTime = 1;
        }
      }
      
      // Create a unique ID for the new element
      const newElementId = `${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
      
      // Check if the new text is just punctuation
      const isPunctuation = /^[.,;:!?"'()\[\]{}…—–-]+$/.test(this.newText.trim());
      
      let newElement;
      if (isPunctuation) {
        // If it's punctuation, create a PunctElement without timing
        newElement = TranscriptElementFactory.createPunctElement(
          `punct-${newElementId}`,
          this.newText
        );
      } else {
        // Otherwise, create a TextElement with timing
        newElement = TranscriptElementFactory.createTextElement(
          `text-${newElementId}`,
          this.newText,
          startTime,
          endTime
        );
      }
      
      // Update internal references
      newElement.updateInternalData(
        this.afterElement.segmentId, 
        this.afterElement.paragraphId, 
        segment.speakerLabel
      );
      
      // Store the new element ID for reference
      this.newElementId = newElementId;
      
      // Insert the element after the specified element
      // Since insertElementAfter doesn't exist, we'll implement it manually
      const insertIndex = paragraphElements.findIndex((e: any) => e.id === this.afterElement.id);
      
      if (insertIndex !== -1) {
        // Insert the new element after the specified element
        paragraphElements.splice(insertIndex + 1, 0, newElement);
        // Rebuild the element map
        paragraph.rebuildElementMap();
      } else {
        // If we can't find the element to insert after, just add it to the end
        paragraph.addElement(newElement);
      }
      
      // Notify observers about the insertion
      this.editor.notifyObservers(TranscriptEditorChangeType.ELEMENT_ADDED, {
        segment,
        paragraph,
        element: newElement,
        afterElementId: this.afterElement.id
      });
      
      return true;
    } catch (error) {
      console.error('Error executing insert text command:', error);
      
      return false;
    } finally {
      // End batch operation regardless of success or failure
      this.editor.endBatch();
    }
  }

  /**
   * Undoes the text insertion
   * 
   * @returns Promise resolving to true if successful
   */
  public async undo(): Promise<boolean> {
    
    try {
      // Start batch operation for better performance
      this.editor.beginBatch();

      // If we don't have a new element ID, we can't undo
      if (!this.newElementId) {
        return false;
      }
      
      // Delete the element we created
      const result = this.editor.deleteElement(
        this.afterElement.segmentId,
        this.afterElement.paragraphId,
        this.newElementId,
        this.userId
      );
      
      return result;
    } catch (error) {
      console.error('Error undoing insert text command:', error);
      
      return false;
    } finally {
      // End batch operation regardless of success or failure
      this.editor.endBatch();
    }
  }
}